
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Info, Activity, Settings, Zap } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow, parseISO } from "date-fns";
import { MotionAlerts } from "./MotionAlerts";
import { formatDeviceId } from "../utils/deviceUtils";

interface DeviceBlockProps {
  deviceId: string;
  deviceName?: string;
}

export default function DeviceBlock({ deviceId, deviceName }: DeviceBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const [lastSeen, setLastSeen] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const queryClient = useQueryClient();
  
  // Debug: Log the device ID this component is handling
  useEffect(() => {
    console.log(`DeviceBlock initialized for device: ${deviceId} (${deviceName || 'unnamed'})`);
  }, [deviceId, deviceName]);

  const { data: deviceStatus, isLoading, error, refetch } = useQuery({
    queryKey: ['device-status', deviceId],
    queryFn: async () => {
      try {
        console.log(`Fetching status for device: ${deviceId}`);
        
        // Check both devices table and recent led_triggers
        const [deviceResult, recentActivityResult] = await Promise.all([
          supabase
            .from('devices')
            .select('*')
            .eq('device_id', deviceId)
            .single(),
          supabase
            .from('led_triggers')
            .select('created_at')
            .eq('device_id', deviceId)
            .order('created_at', { ascending: false })
            .limit(1)
        ]);
        
        const { data: deviceData, error: deviceError } = deviceResult;
        const { data: recentActivity, error: activityError } = recentActivityResult;
        
        if (deviceError) {
          console.warn(`Device not found in devices table: ${deviceError.message}`);
          setDebugInfo(`Device table: Not found (${deviceError.message})`);
        } else {
          console.log(`Device status for ${deviceId}:`, deviceData);
          setDebugInfo(`Device table: Found, last_seen: ${deviceData?.last_seen || 'Never'}`);
        }
        
        if (recentActivity && recentActivity.length > 0) {
          console.log(`Recent activity for ${deviceId}:`, recentActivity[0]);
          setDebugInfo(prev => `${prev} | Recent activity: ${recentActivity[0].created_at}`);
          
          // If we have recent activity but no device record, use activity timestamp
          if (!deviceData?.last_seen) {
            const activityDate = parseISO(recentActivity[0].created_at);
            setLastSeen(formatDistanceToNow(activityDate, { addSuffix: true }));
            
            // Create a combined status object
            return {
              device_id: deviceId,
              name: deviceName,
              last_seen: recentActivity[0].created_at,
              location: 'Unknown',
              hardware_type: 'ESP32',
              ip_address: 'Unknown'
            };
          }
        } else {
          console.log(`No recent activity found for ${deviceId}`);
          setDebugInfo(prev => `${prev} | Recent activity: None`);
        }
        
        if (deviceData?.last_seen) {
          const lastSeenDate = parseISO(deviceData.last_seen);
          setLastSeen(formatDistanceToNow(lastSeenDate, { addSuffix: true }));
        }
        
        return deviceData;
      } catch (err: any) {
        console.error(`Error fetching status for device ${deviceId}:`, err);
        setDebugInfo(`Error: ${err.message}`);
        throw err;
      }
    },
    refetchInterval: 30000
  });

  // No realtime subscription here: device check-ins update every ~2 minutes and
  // a per-device live channel multiplied realtime usage. The 30s refetch above
  // keeps this tile current.

  const handleRefresh = () => {
    refetch();
    // Also invalidate related queries
    queryClient.invalidateQueries({ queryKey: ['devices'] });
    toast.success("Refreshing device status");
  };

  const sendTestCommand = async () => {
    try {
      const { error } = await supabase
        .from('device_commands')
        .insert({
          device_id: deviceId,
          command_type: 'test_led',
          params: JSON.stringify({
            duration: 3000,
            color: 'blue'
          }),
          processed: false
        });
        
      if (error) throw error;
      
      toast.success("Test command sent to device", {
        description: "The device LED should blink if it's online"
      });
    } catch (err: any) {
      console.error("Error sending test command:", err);
      toast.error("Failed to send test command", {
        description: err.message
      });
    }
  };

  const displayName = deviceName || `Device ${formatDeviceId(deviceId, true)}`;
  const formattedId = formatDeviceId(deviceId, false);
  
  // More generous online detection - consider online if last seen within 5 minutes OR recent activity
  const isOnline = deviceStatus?.last_seen 
    ? (new Date().getTime() - new Date(deviceStatus.last_seen).getTime() < 300000) // 5 minutes
    : false;

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-slate-50 pb-3">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg">{displayName}</CardTitle>
            <CardDescription className="mt-1 font-mono">
              {formattedId}
              {debugInfo && (
                <div className="text-xs text-orange-600 mt-1 bg-orange-50 p-1 rounded">
                  {debugInfo}
                </div>
              )}
            </CardDescription>
          </div>
          <Badge variant={isOnline ? "default" : "secondary"} className="mt-1">
            {isOnline ? "Online" : "Offline"}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        <div className="p-4 border-b">
          <div className="grid grid-cols-2 gap-2 text-sm mb-3">
            <div className="text-muted-foreground">Status:</div>
            <div>{isOnline ? "Connected" : "Disconnected"}</div>
            
            <div className="text-muted-foreground">Last seen:</div>
            <div>{lastSeen || "Unknown"}</div>

            <div className="text-muted-foreground">Location:</div>
            <div>{deviceStatus?.location || "Not set"}</div>
          </div>
          
          <div className="flex space-x-2 mt-4">
            <Button 
              variant="outline" 
              size="sm" 
              className="flex items-center gap-1"
              onClick={handleRefresh}
            >
              <Activity className="h-4 w-4" />
              Refresh
            </Button>
            
            <Button 
              variant="outline" 
              size="sm"
              className="flex items-center gap-1"
              onClick={sendTestCommand}
            >
              <Zap className="h-4 w-4" />
              Test LED
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 ml-auto"
              onClick={() => setExpanded(!expanded)}
            >
              <Info className="h-4 w-4" />
              {expanded ? "Less" : "More"}
            </Button>
          </div>
        </div>
        
        {expanded && (
          <div className="p-4">
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                <Settings className="h-4 w-4" />
                Device Details
              </h4>
              <div className="text-xs space-y-1 text-muted-foreground">
                <p>Hardware: {deviceStatus?.hardware_type || "ESP32"}</p>
                <p>IP: {deviceStatus?.ip_address || "Unknown"}</p>
                <p>MAC Address: {deviceId}</p>
              </div>
            </div>
            
            <MotionAlerts deviceId={deviceId} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
